import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { MessageSquare, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const getWeekStart = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
  const monday = new Date(d.setDate(diff));
  return monday.toISOString().split('T')[0];
};

const formatWeekRange = (weekStart) => {
  const start = new Date(weekStart);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', opts)}`;
};

const WeeklyBestMessage = () => {
  const { user, isReadOnly } = useAuth();
  const isAdminOrTL = ['admin', 'team_lead'].includes(user?.role);
  // Viewer (co-founder) gets the team-wide view (same as admin/TL) but
  // not the submission form — they don't write LinkedIn messages.
  const showTeamView = isAdminOrTL || isReadOnly;

  const [messageText, setMessageText] = useState('');
  const [targetIndustries, setTargetIndustries] = useState('');
  const [contextNotes, setContextNotes] = useState('');
  const [mySavedThisWeek, setMySavedThisWeek] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [allMessages, setAllMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentWeek = getWeekStart();

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await api.get('/best-messages');
      setAllMessages(res.data?.messages || []);

      // Pre-fill form with current user's current-week submission if any
      const myCurrent = (res.data?.messages || []).find(
        (m) => m.user_id === user.id && m.week_start?.startsWith(currentWeek)
      );
      if (myCurrent) {
        setMessageText(myCurrent.message_text || '');
        setTargetIndustries(myCurrent.target_industries || '');
        setContextNotes(myCurrent.context_notes || '');
        setMySavedThisWeek(myCurrent);
      } else {
        setMySavedThisWeek(null);
      }
    } catch (err) {
      console.error('Fetch best messages error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) {
      setError('Message text is required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await api.post('/best-messages', {
        week_start: currentWeek,
        message_text: messageText.trim(),
        target_industries: targetIndustries.trim(),
        context_notes: contextNotes.trim(),
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      fetchMessages();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  // Partition messages for rendering
  const myPastMessages = allMessages
    .filter((m) => m.user_id === user.id && !m.week_start?.startsWith(currentWeek))
    .slice(0, 4);

  const currentWeekTeam = allMessages.filter((m) =>
    m.week_start?.startsWith(currentWeek)
  );

  const pastWeeksTeam = allMessages.filter(
    (m) => !m.week_start?.startsWith(currentWeek)
  );

  return (
    <div>
      <div className="page-header">
        <h2>Weekly Best Message</h2>
        <p>Submit your best-performing message for {formatWeekRange(currentWeek)}</p>
        {!showTeamView && mySavedThisWeek && (
          <div style={{ marginTop: 8, padding: '8px 12px', background: '#DCFCE7', borderLeft: '3px solid #166534', borderRadius: 4, fontSize: 13, color: '#166534', display: 'inline-block' }}>
            ✓ You've submitted for this week. Edit the form below and save to update.
          </div>
        )}
      </div>

      {/* Submission form — everyone can submit their own, except read-only viewers */}
      {!isReadOnly && (
      <div className="card">
        <div className="card-header">
          <h3>
            <MessageSquare size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Your Best Message This Week
          </h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
              Message text <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Paste the message that worked best for you this week..."
              rows={5}
              style={{ width: '100%', padding: 10, border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
              Target industries
            </label>
            <input
              type="text"
              value={targetIndustries}
              onChange={(e) => setTargetIndustries(e.target.value)}
              placeholder="e.g., Healthcare, FinTech, Real Estate"
              style={{ width: '100%', padding: 10, border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14 }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
              Context notes
            </label>
            <textarea
              value={contextNotes}
              onChange={(e) => setContextNotes(e.target.value)}
              placeholder="Why did this message work? What was the hook? How many accepts/replies did it generate?"
              rows={3}
              style={{ width: '100%', padding: 10, border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }}
            />
          </div>

          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#DC2626', fontSize: 13, marginBottom: 12 }}>
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '8px 20px',
              background: savedFlash ? '#166534' : '#1F3A5F',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: 6,
              fontSize: 14,
              cursor: saving ? 'default' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {savedFlash ? <><Check size={14} /> Saved</> : (saving ? 'Saving...' : 'Save This Week')}
          </button>
        </form>
      </div>
      )}

      {/* Admin / TL view: team side-by-side for current week */}
      {showTeamView && currentWeekTeam.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3>Team — This Week ({formatWeekRange(currentWeek)})</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
            {currentWeekTeam.map((m) => (
              <div key={m.id} style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{m.bde_name}</div>
                {m.target_industries && (
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
                    Industries: {m.target_industries}
                  </div>
                )}
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, background: '#F9FAFB', padding: 10, borderRadius: 4, marginBottom: 8 }}>
                  {m.message_text}
                </div>
                {m.context_notes && (
                  <div style={{ fontSize: 12, color: '#374151' }}>
                    <strong>Notes:</strong> {m.context_notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BDE view: their current-week saved submission for reference */}
      {!showTeamView && mySavedThisWeek && (
        <div className="card">
          <div className="card-header">
            <h3>Your Saved Submission — This Week</h3>
          </div>
          <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
            {mySavedThisWeek.target_industries && (
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
                Industries: {mySavedThisWeek.target_industries}
              </div>
            )}
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, background: '#F9FAFB', padding: 10, borderRadius: 4, marginBottom: 8 }}>
              {mySavedThisWeek.message_text}
            </div>
            {mySavedThisWeek.context_notes && (
              <div style={{ fontSize: 12, color: '#374151' }}>
                <strong>Notes:</strong> {mySavedThisWeek.context_notes}
              </div>
            )}
          </div>
        </div>
      )}

      {/* BDE view: their own past 4 weeks */}
      {!showTeamView && myPastMessages.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3>Your Past Submissions</h3>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Week</th><th>Industries</th><th>Message</th>
                </tr>
              </thead>
              <tbody>
                {myPastMessages.map((m) => (
                  <tr key={m.id}>
                    <td>{formatWeekRange(m.week_start)}</td>
                    <td>{m.target_industries || '—'}</td>
                    <td style={{ maxWidth: 500, whiteSpace: 'pre-wrap' }}>{m.message_text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Admin / TL view: history table */}
      {showTeamView && pastWeeksTeam.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3>Team — Past Weeks</h3>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Week</th><th>BDE</th><th>Industries</th><th>Message</th>
                </tr>
              </thead>
              <tbody>
                {pastWeeksTeam.map((m) => (
                  <tr key={m.id}>
                    <td>{formatWeekRange(m.week_start)}</td>
                    <td><strong>{m.bde_name}</strong></td>
                    <td>{m.target_industries || '—'}</td>
                    <td style={{ maxWidth: 400, whiteSpace: 'pre-wrap' }}>{m.message_text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && <div className="empty-state"><p>Loading...</p></div>}
    </div>
  );
};

export default WeeklyBestMessage;